/**
 * Cart Component
 * 
 * This component displays the shopping cart sidebar with all added products,
 * quantity controls, and checkout functionality.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, Store, Truck } from 'lucide-react';
import Button from '../Button/Button';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../contexts/ToastContext';
import { neo4jService } from '../../services/neo4jService';
import './Cart.css';

/**
 * Cart component for displaying and managing cart items
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the cart sidebar is open
 * @param {Function} props.onClose - Function to close the cart sidebar
 */
const Cart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // Get cart state and functions from context
  const { 
    cartItems, 
    updateQuantity, 
    removeItem,
    clearCart
  } = useCart();
  const [showCheckoutOptions, setShowCheckoutOptions] = useState(false);
  const [fulfillmentType, setFulfillmentType] = useState('pickup');
  const [pickupDetails, setPickupDetails] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Calculate total price of all items in cart
  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setPickupDetails(details => ({
      ...details,
      name: details.name || currentUser.name || '',
      email: details.email || currentUser.email || ''
    }));
  }, [currentUser]);

  /**
   * Handles the checkout process
   * Shows fulfillment choices before placing a pickup order.
   */
  const handleCheckout = () => {
    if (!currentUser) {
      toast.info('Please log in before checkout');
      onClose();
      navigate('/login');
      return;
    }

    setShowCheckoutOptions(true);
  };

  const handlePickupDetailChange = (event) => {
    const { name, value } = event.target;
    setPickupDetails(details => ({ ...details, [name]: value }));
  };

  const handlePlacePickupOrder = async () => {
    if (
      !pickupDetails.name.trim() ||
      !pickupDetails.phone.trim() ||
      !pickupDetails.email.trim() ||
      !pickupDetails.date ||
      !pickupDetails.time
    ) {
      toast.error('Please add a pickup name, phone, email, date, and time');
      return;
    }

    try {
      setSubmitting(true);
      const order = await neo4jService.createPickupOrder(currentUser.id || currentUser.email, cartItems, pickupDetails);
      clearCart();
      setShowCheckoutOptions(false);
      toast.success(`Pickup order ${order.orderId} was created`);
      onClose();
      navigate('/orders');
    } catch (error) {
      console.error('Failed to create pickup order:', error);
      toast.error('Failed to create pickup order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`cart-overlay ${isOpen ? 'active' : ''}`} onClick={onClose}>
      <div className={`cart-drawer ${isOpen ? 'open' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Cart header with title and close button */}
        <div className="cart-header">
          <h2>Shopping Cart ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items)</h2>
          <Button className="close-button" onClick={onClose}>
            <X size={24} />
          </Button>
        </div>

        {/* Cart items list or empty cart message */}
        <div className="cart-items">
          {cartItems.length > 0 ? (
            cartItems.map(item => (
              <div key={item.id} className="cart-item">
                <img src={item.image} alt={item.title} />
                <div className="item-details">
                  <h3>{item.title || item.name}</h3>
                  <div className="item-price">${(item.price * item.quantity).toFixed(2)}</div>
                  {/* Quantity adjustment controls */}
                  <div className="quantity-controls">
                    <Button onClick={() => updateQuantity(item.id, -1)} disabled={item.quantity <= 1}>
                      <Minus size={14} />
                    </Button>
                    <span>{item.quantity}</span>
                    <Button onClick={() => updateQuantity(item.id, 1)} disabled={item.quantity >= 99}>
                      <Plus size={14} />
                    </Button>
                  </div>
                </div>
                {/* Remove item button */}
                <Button 
                  className="remove-button"
                  onClick={() => removeItem(item.id)}
                  title="Remove item"
                >
                  <X size={18} />
                </Button>
              </div>
            ))
          ) : (
            <div className="empty-cart">
              <ShoppingBag size={48} />
              <p>Your cart is empty</p>
            </div>
          )}
        </div>

        {/* Cart footer with totals and checkout button */}
        <div className="cart-footer">
          <div className="cart-subtotal">
            <span>Subtotal</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <div className="cart-total">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
          {showCheckoutOptions && (
            <div className="checkout-options">
              <div className="checkout-option-header">
                <h3>Checkout Method</h3>
              </div>
              <div className="fulfillment-options">
                <label className={`fulfillment-option ${fulfillmentType === 'pickup' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="fulfillmentType"
                    value="pickup"
                    checked={fulfillmentType === 'pickup'}
                    onChange={() => setFulfillmentType('pickup')}
                  />
                  <Store size={18} />
                  <span>In-store pickup</span>
                </label>
                <label className={`fulfillment-option ${fulfillmentType === 'shipping' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="fulfillmentType"
                    value="shipping"
                    checked={fulfillmentType === 'shipping'}
                    onChange={() => setFulfillmentType('shipping')}
                  />
                  <Truck size={18} />
                  <span>Shipping</span>
                </label>
              </div>

              {fulfillmentType === 'pickup' ? (
                <div className="pickup-form">
                  <label>
                    Pickup name
                    <input
                      type="text"
                      name="name"
                      value={pickupDetails.name}
                      onChange={handlePickupDetailChange}
                    />
                  </label>
                  <label>
                    Phone
                    <input
                      type="tel"
                      name="phone"
                      value={pickupDetails.phone}
                      onChange={handlePickupDetailChange}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      value={pickupDetails.email}
                      onChange={handlePickupDetailChange}
                    />
                  </label>
                  <div className="pickup-schedule-fields">
                    <label>
                      Pickup date
                      <input
                        type="date"
                        name="date"
                        value={pickupDetails.date}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={handlePickupDetailChange}
                      />
                    </label>
                    <label>
                      Pickup time
                      <input
                        type="time"
                        name="time"
                        value={pickupDetails.time}
                        onChange={handlePickupDetailChange}
                      />
                    </label>
                  </div>
                  <label>
                    Notes
                    <textarea
                      name="notes"
                      value={pickupDetails.notes}
                      onChange={handlePickupDetailChange}
                      rows="2"
                      placeholder="Preferred pickup day or special instructions"
                    />
                  </label>
                </div>
              ) : (
                <div className="shipping-message">
                  Shipping checkout is coming soon. Choose in-store pickup to place this order now.
                </div>
              )}
            </div>
          )}
            <Button 
                className="checkout-button" 
                disabled={cartItems.length === 0 || submitting || (showCheckoutOptions && fulfillmentType === 'shipping')}
                onClick={showCheckoutOptions && fulfillmentType === 'pickup' ? handlePlacePickupOrder : handleCheckout}
            >
            {showCheckoutOptions && fulfillmentType === 'pickup'
              ? (submitting ? 'Creating Pickup Order...' : 'Place Pickup Order')
              : 'Proceed to Checkout'}
            </Button>
        </div>
      </div>
    </div>
  );
};

export default Cart;
